import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  SupplierMarkupPayload,
  SupplierMarkup,
  FlightMarkupFilter,
  FlightMarkupListResponse,
} from "../types/flight-markups.types";

type FlightMarkupDetailResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: SupplierMarkup;
};

type FlightMarkupMutationResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: unknown;
};

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.markups;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const flightMarkupsService = {
  async list(
    filter: FlightMarkupFilter,
  ): Promise<FlightMarkupListResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<FlightMarkupListResponse>(
        endpoints.list(params.toString()),
        { method: "GET", isAuthenticated: true, cache: "no-store" },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to load flight markups due to an unexpected error.",
      ) as unknown as FlightMarkupListResponse;
    }
  },

  async details(id: number | string): Promise<FlightMarkupDetailResponse> {
    try {
      return await apiClient<FlightMarkupDetailResponse>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load flight markup details due to an unexpected error.",
      ) as unknown as FlightMarkupDetailResponse;
    }
  },

  // markup_amount_type + markup_amount are required, and at least one of
  // supplier_code / airline_code / route_type / trip_type / cabin_class
  async create(
    payload: Partial<SupplierMarkupPayload> & { status?: "active" | "inactive" },
  ): Promise<FlightMarkupMutationResponse> {
    try {
      return await apiClient<FlightMarkupMutationResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to create flight markup due to an unexpected error.",
      ) as FlightMarkupMutationResponse;
    }
  },

  async update(
    id: number | string,
    payload: Partial<SupplierMarkupPayload> & { status?: "active" | "inactive" },
  ): Promise<FlightMarkupMutationResponse> {
    try {
      return await apiClient<FlightMarkupMutationResponse>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update flight markup due to an unexpected error.",
      ) as FlightMarkupMutationResponse;
    }
  },

  async updateStatus(
    id: number | string,
    payload: { status: "active" | "inactive" },
  ): Promise<FlightMarkupMutationResponse> {
    return flightMarkupsService.update(id, payload);
  },

  async delete(id: number | string): Promise<FlightMarkupMutationResponse> {
    try {
      return await apiClient<FlightMarkupMutationResponse>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to delete flight markup due to an unexpected error.",
      ) as FlightMarkupMutationResponse;
    }
  },
};
